import type { GetListParams, GetListResult, GetOneParams, GetOneResult } from "react-admin";
import { DateTime } from "luxon";
import { z } from "zod";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { AbstractDataProvider } from "./AbstractDataProvider";

export type ServiceEntry = z.infer<typeof schemas.ServiceEntryDto>;

class ServiceEntriesProvider extends AbstractDataProvider<ServiceEntry> {
  async getOne(_resource: string, { id }: GetOneParams): Promise<GetOneResult<ServiceEntry>> {
    const client = await getAuthenticatedServerClient();
    return { data: await client.getServiceEntry({ params: { id: String(id) } }) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<ServiceEntry>> {
    const { filter, sort, pagination } = params;
    if (!filter.drivingSchoolId) {
      throw new Error("Bitte wähle eine Fahrschule aus.");
    }
    const filters: z.infer<typeof schemas.SingleFilter>[] = [
      { field: "drivingSchoolId", type: "eqstring", value: filter.drivingSchoolId },
    ];
    for (const field of ["status", "studentId", "drivingLicenseClass"] as const) {
      if (filter[field]) {
        filters.push({ field, type: "eqstring", value: filter[field] });
      }
    }
    for (const field of ["dateFrom", "dateUntil"] as const) {
      if (!filter[field]) {
        continue;
      }
      const date = DateTime.fromISO(filter[field], { zone: "Europe/Berlin" }).startOf("day");
      if (!date.isValid) {
        throw new Error("Bitte gib ein gültiges Leistungsdatum ein.");
      }
      filters.push({
        field: "serviceDate",
        type: field === "dateFrom" ? ">=" : "<",
        value: (field === "dateFrom" ? date : date.plus({ days: 1 })).toUTC().toISO()!,
      });
    }
    const client = await getAuthenticatedServerClient();
    let total: number | undefined;
    client.use({
      response: async (_api, _config, response) => {
        const range = response.headers["content-range"];
        const match = typeof range === "string" ? /\/(\d+)$/.exec(range) : null;
        if (match) {
          total = Number(match[1]);
        }
        return response;
      },
    });
    const data = await client.listServiceEntries({
      queries: {
        filter: filters,
        order: [
          { field: sort.field, dir: sort.order === "ASC" ? "asc" : "desc" },
          ...(sort.field === "id" ? [] : [{ field: "id", dir: "asc" as const }]),
        ],
        offset: (pagination.page - 1) * pagination.perPage,
        limit: pagination.perPage,
      },
    });
    if (total === undefined) {
      throw new Error("Die Anzahl der Leistungen konnte nicht geladen werden.");
    }
    return { data, total };
  }
}

export const serviceEntriesProvider = new ServiceEntriesProvider();
